import React from 'react';
import { TextInput } from 'ui/TextInput';
import FluidButton from 'ui/FluidButton';
import { CircleIconButton } from 'ui/CircleIconButton';
import XIcon from 'ui/Icons/tabler/x.component.svg';
import SingleDateInput from 'pureUi/SingleDateInput';
import { PriceAsCentsInput } from 'ui/stateful/PriceAsCentsInput';
import { formatDate } from 'utils';

export interface IInvoiceDueDateRow {
  id: string;
  date: string | null;
  percentage: number | null;
  amountCents: number | null;
}

interface IInvoiceDueDateErrors {
  date?: string;
  percentage?: string;
  amountCents?: string;
}

interface IInvoiceDueDatesEditorProps {
  dueDates: IInvoiceDueDateRow[];
  errors: Record<string, IInvoiceDueDateErrors>;
  sectionErrors: string[];
  onUpdateDate: (index: number, date: string) => void;
  onUpdatePercentage: (index: number, percentage: number | null) => void;
  onUpdateAmount: (index: number, amountCents: number | null) => void;
  onRemoveRow: (index: number) => void;
  onAddRow: () => void;
}

export const InvoiceDueDatesEditor = ({
  dueDates,
  errors,
  sectionErrors,
  onUpdateDate,
  onUpdatePercentage,
  onUpdateAmount,
  onRemoveRow,
  onAddRow,
}: IInvoiceDueDatesEditorProps) => {
  const hasRows = dueDates.length > 0;

  return (
    <div>
      {/* Column headers — only visible when rows exist */}
      {hasRows && (
        <div className="grid grid-cols-[120px_120px_1fr_32px] gap-[10px] mb-[5px]">
          <span className="font-pt-sans text-[13px] text-black">Date *</span>
          <span className="font-pt-sans text-[13px] text-black">Percentage (%) *</span>
          <span className="font-pt-sans text-[13px] text-black">Amount *</span>
          <span />
        </div>
      )}

      {/* Rows */}
      {dueDates.map((row, index) => {
        const rowErrors = errors[row.id] ?? {};
        return (
          <div key={row.id} className="grid grid-cols-[120px_120px_1fr_32px] gap-[10px] mb-[8px] items-start">
            <div>
              <SingleDateInput
                value={row.date ? new Date(row.date) : null}
                onChange={date => onUpdateDate(index, formatDate(date))}
                enablePastDates
                showYearDropdown
                placeholder="Select Date ..."
                buttonClassName={`!h-[35px] ${rowErrors.date ? '!border-red-95 !bg-red-25' : ''}`}
              />
              {rowErrors.date && (
                <span className="font-pt-sans text-[13px] text-red-95 block mt-[2px]">{rowErrors.date}</span>
              )}
            </div>
            <div>
              <TextInput
                id={`due-date-percentage-${row.id}`}
                type="number"
                value={row.percentage !== null ? String(row.percentage) : ''}
                onChange={e => {
                  const val = e.currentTarget.value;
                  onUpdatePercentage(index, val === '' ? null : parseFloat(val));
                }}
                className="w-[120px]"
                inputClassName={`!h-[35px] !mt-0 !py-0 !px-[8px] ${
                  rowErrors.percentage ? '!border-red-95 !bg-red-25' : ''
                }`}
              />
              {rowErrors.percentage && (
                <span className="font-pt-sans text-[13px] text-red-95 block mt-[2px]">{rowErrors.percentage}</span>
              )}
            </div>
            <div>
              <PriceAsCentsInput
                cents={row.amountCents ?? undefined}
                onBlurUpdate={cents => onUpdateAmount(index, cents ?? null)}
                disabled={false}
                isAllowUndefined
                className={`w-full h-[35px] border border-solid px-[8px] font-pt-sans text-[15px] text-black ${
                  rowErrors.amountCents ? 'border-red-95 bg-red-25' : 'border-gray-40 bg-white'
                }`}
              />
              {rowErrors.amountCents && (
                <span className="font-pt-sans text-[13px] text-red-95 block mt-[2px]">{rowErrors.amountCents}</span>
              )}
            </div>
            <div className="flex items-center justify-center h-[35px]">
              <CircleIconButton
                type="secondary"
                disabled={false}
                IconComponent={XIcon}
                iconSize="default"
                onClick={() => onRemoveRow(index)}
              />
            </div>
          </div>
        );
      })}

      {sectionErrors.map(error => (
        <span key={error} className="font-pt-sans text-[13px] text-red-95 block mb-[4px]">
          {error}
        </span>
      ))}

      <FluidButton type="secondary" onClick={onAddRow}>
        Add
      </FluidButton>
    </div>
  );
};
